import { Message } from '../types';

export interface MessageGroup {
  id: string;
  messages: Message[];
  showTimestamp: boolean;
  combinedThinking?: string;
  hasThinking: boolean;
}

/**
 * Groups messages by sender and time proximity, combining thinking content
 */
export const groupMessages = (messages: Message[]): MessageGroup[] => {
  const grouped: MessageGroup[] = [];
  
  messages.forEach((message, index) => {
    const prevMessage = messages[index - 1];
    const shouldGroup = prevMessage && 
      prevMessage.sender === message.sender &&
      (new Date(message.timestamp).getTime() - new Date(prevMessage.timestamp).getTime()) < 5 * 60 * 1000; // 5 minutes

    if (shouldGroup && grouped.length > 0) {
      // Add to existing group
      const currentGroup = grouped[grouped.length - 1];
      currentGroup.messages.push(message);
      
      // Combine thinking content
      if (message.thinking) {
        currentGroup.combinedThinking = (currentGroup.combinedThinking || '') + 
          (currentGroup.combinedThinking ? '\n\n' : '') + message.thinking;
        currentGroup.hasThinking = true;
      }
    } else {
      // Start new group
      const newGroup: MessageGroup = {
        id: `group-${message.id}`,
        messages: [message],
        showTimestamp: true,
        combinedThinking: message.thinking || undefined,
        hasThinking: Boolean(message.thinking),
      };
      grouped.push(newGroup);
    }
  });

  return grouped;
};

/**
 * Combines multiple thinking messages into a single coherent text
 */
export const combineThinkingContent = (messages: Message[]): string => {
  const thinkingParts = messages
    .filter(msg => msg.thinking)
    .map(msg => msg.thinking)
    .filter(Boolean);
  
  return thinkingParts.join('\n\n');
};

/**
 * Gets the main content from a group of messages, excluding thinking
 */
export const getCombinedContent = (messages: Message[]): string => {
  return messages
    .map(msg => msg.content)
    .filter(Boolean)
    .join(' ');
};

/**
 * Checks if any message in the group is still streaming
 */
export const isGroupStreaming = (messages: Message[]): boolean => {
  return messages.some(msg => msg.isStreaming);
};